// @spec SUP-STR-028
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PortalProfilePage,
  PortalProfileLoadingSkeleton,
  usePortalProfile,
  useSubmitProfileChanges,
  useUploadCertification,
  useCancelPendingChange,
} from '@/features/portal/profile';

export const Route = createFileRoute('/portal/profile/$token')({
  component: PortalProfilePageRoute,
});

interface ErrorStateProps {
  title: string;
  message: string;
  icon: React.ReactNode;
}

/**
 * Error state component.
 */
function ErrorState({ title, message, icon }: ErrorStateProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{message}</p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Portal profile page route.
 *
 * This is a PUBLIC page that allows suppliers to view and edit their profile
 * using a magic link token.
 */
function PortalProfilePageRoute() {
  const params = Route.useParams();
  const token = params.token;
  const { data, isLoading, isError, error } = usePortalProfile(token);
  const submitProfileChanges = useSubmitProfileChanges(token);
  const uploadCertification = useUploadCertification(token);
  const cancelPendingChange = useCancelPendingChange(token);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <PortalProfileLoadingSkeleton />
        </div>
      </div>
    );
  }

  // Error states
  if (isError || !data) {
    const typedError = error as (Error & { code?: string }) | null;
    const errorCode = typedError?.code;

    if (errorCode === 'TOKEN_EXPIRED') {
      return (
        <ErrorState
          title="Link Expired"
          message="This portal link has expired. Please request a new link from the organization."
          icon={<Clock className="h-8 w-8 text-muted-foreground" />}
        />
      );
    }

    return (
      <ErrorState
        title="Link Invalid"
        message="This portal link is invalid. Please check the link and try again."
        icon={<AlertCircle className="h-8 w-8 text-muted-foreground" />}
      />
    );
  }

  // Render portal profile page
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <PortalProfilePage
          data={data}
          onSubmitProfileChanges={(changes) => submitProfileChanges.mutateAsync(changes)}
          onSubmitContactChanges={(changes) => submitProfileChanges.mutateAsync(changes)}
          onUploadCertification={(formData) => uploadCertification.mutateAsync(formData)}
          onCancelPendingChange={(changeId) => cancelPendingChange.mutateAsync(changeId)}
        />
      </div>
    </div>
  );
}
